import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plant, ArrowRight, Warning, Storefront, Handshake, Package, Airplane } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import useStates from "@/lib/useStates";
import { useI18n } from "@/context/I18nContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { IndiaMandiMark } from "@/components/Logo";
import { formatErr } from "@/lib/api";

const ROLES = [
  { key: "farmer", label: "Farmer / Manufacturer", icon: Plant },
  { key: "mandi", label: "Mandi (APMC)", icon: Storefront },
  { key: "trader", label: "Trader", icon: Handshake },
  { key: "wholesaler", label: "Super Stockist / Wholesaler", icon: Package },
  { key: "exporter", label: "Exporter / Importer", icon: Airplane },
];

const AUTH_BG = "https://images.unsplash.com/photo-1558388556-2261d4cc1938?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Register() {
  const { t } = useI18n();
  const { register } = useAuth();
  const nav = useNavigate();
  const states = useStates();
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    role: "farmer", state: "", district: "", pincode: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await register(form);
      toast.success(`Welcome to India Mandi, ${u.name}!`);
      nav("/app");
    } catch (ex) {
      setErr(formatErr(ex?.response?.data?.detail) || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-stone-50" data-testid="register-page">
      <div className="flex flex-col p-6 sm:p-12 overflow-y-auto">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <IndiaMandiMark size={38} />
          </Link>
          <LanguageToggle />
        </div>
        <div className="flex-1 py-8">
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            onSubmit={submit}
            className="w-full max-w-lg mx-auto"
          >
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Sign up</div>
            <h1 className="font-display text-4xl font-black tracking-tight text-emerald-950 mt-2">{t("auth.register_title")}</h1>
            <p className="text-stone-600 mt-2">{t("auth.register_sub")}</p>

            <div className="mt-6">
              <label className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("auth.role_pick")}</label>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {ROLES.map((r) => (
                  <button
                    key={r.key} type="button" onClick={() => set("role", r.key)}
                    className={`p-3 rounded-md border text-left transition-colors ${form.role === r.key ? "border-emerald-700 bg-emerald-50" : "border-stone-200 hover:border-stone-300 bg-white"}`}
                    data-testid={`register-role-${r.key}`}
                  >
                    <r.icon weight="duotone" size={22} className={form.role === r.key ? "text-emerald-800" : "text-stone-500"} />
                    <div className="text-xs font-bold mt-1 text-stone-800">{r.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <Field label={t("auth.name")} required value={form.name} onChange={(v) => set("name", v)} testid="register-name-input" />
              <Field label={t("auth.phone")} value={form.phone} onChange={(v) => set("phone", v)} testid="register-phone-input" />
              <Field label={t("auth.email")} type="email" required value={form.email} onChange={(v) => set("email", v)} testid="register-email-input" />
              <Field label={t("auth.password")} type="password" required value={form.password} onChange={(v) => set("password", v)} testid="register-password-input" />
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("auth.state")}</label>
                <select value={form.state} onChange={(e) => set("state", e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-md border border-stone-300 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100 outline-none bg-white text-sm" data-testid="register-state-input">
                  <option value="" />
                  {states.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <Field label={t("auth.district")} value={form.district} onChange={(v) => set("district", v)} testid="register-district-input" />
              <Field label={t("auth.pincode")} value={form.pincode} onChange={(v) => set("pincode", v)} testid="register-pincode-input" />
            </div>
            <p className="mt-2 text-xs text-stone-500">Password: at least 8 characters, with letters and digits. PIN code: 6 digits.</p>

            {err && (
              <div className="mt-4 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="register-error">
                <Warning weight="fill" size={16} /> {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-bold transition-colors"
              data-testid="register-submit-btn"
            >
              {busy ? "Creating…" : t("auth.register_btn")} <ArrowRight size={18} weight="bold" />
            </button>

            <p className="mt-6 text-sm text-stone-600">
              {t("auth.have_account")} <Link to="/login" className="font-bold text-emerald-900 hover:text-orange-600" data-testid="link-login">{t("auth.login_btn")}</Link>
            </p>
          </motion.form>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <img src={AUTH_BG} alt="India agriculture" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-emerald-950 via-emerald-950/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-14 text-white">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400 mb-4">One India. One Mandi.</div>
          <p className="font-display text-3xl leading-snug font-bold max-w-lg">
            Join 5,000+ verified farmers, traders and exporters weaving Bharat into a single, trusted marketplace.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest text-stone-500">{label}</label>
      <input
        type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2.5 rounded-md border border-stone-300 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100 outline-none bg-white text-sm"
        data-testid={testid}
      />
    </div>
  );
}
